import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { firstValueFrom } from 'rxjs';
import { GameHome } from '../models/game-home.model';
import { isRemoteLogoUrl, normalizeLogoKey, resolveGameLogoSrc } from '../utils/game-logo.util';

/** UI name / slug / path → gamesHome.product */
const UI_TO_PRODUCT: Record<string, string> = {
  melate: 'Melate',
  melaterevancha: 'Melate',
  'melate-revancha': 'Melate',
  'melate-revancha-revanchita': 'Melate',
  melaterevancharevanchita: 'Melate',
  melateretro: 'MelateRetro',
  'melate-retro': 'MelateRetro',
  'melate retro': 'MelateRetro',
  chispazo: 'Chispazo',
  tris: 'Tris',
  tri$: 'Tris',
  ganagato: 'GanaGato',
  'gana-gato': 'GanaGato',
  'gana gato': 'GanaGato',
  progol: 'Progol',
  progolrevancha: 'Progol',
  progolmedia: 'ProgolMediaSemana',
  'progol-media-semana': 'ProgolMediaSemana',
  'progol media semana': 'ProgolMediaSemana',
  'progol ms': 'ProgolMediaSemana',
  pronosports: 'MiProgol',
  'prono-sports': 'MiProgol',
  'prono sports': 'MiProgol',
  protouch: 'Protouch',
};

/**
 * Caches electronic logos from gamesHome.logo (Firebase Storage URLs)
 * keyed primarily by product (Melate vs MelateRetro, etc.).
 */
@Injectable({
  providedIn: 'root',
})
export class GameLogoService {
  private byKey = new Map<string, string>();
  private games: GameHome[] = [];
  private ready: Promise<void>;

  constructor(private afs: AngularFirestore) {
    this.ready = this.load();
  }

  private async load(): Promise<void> {
    try {
      const games = await firstValueFrom(
        this.afs
          .collection<GameHome>('gamesHome', (ref) => ref.where('isActive', '==', true))
          .valueChanges({ idField: 'id' })
      );
      this.games = games;
      this.byKey.clear();

      for (const g of games) {
        const url = isRemoteLogoUrl(g.dynamicLogoUrl) ? g.dynamicLogoUrl : isRemoteLogoUrl(g.logo) ? g.logo : undefined;
        if (!url) continue;

        // Product is the source of truth (Melate ≠ MelateRetro)
        if (g.product) {
          this.setKey(g.product, url);
          this.applyProductAliases(g.product, url);
        }

        if (g.name) {
          this.setKey(g.name, url);
          const mapped = this.resolveProductKey(g.name);
          if (mapped) this.setKeyIfEmpty(mapped, url);
        }
      }

      // Extra aliases from product → UI slugs
      this.copyAlias('Melate', 'melaterevancha');
      this.copyAlias('Melate', 'melate-revancha-revanchita');
      this.copyAlias('MelateRetro', 'melate-retro');
      this.copyAlias('MelateRetro', 'melateretro');
      this.copyAlias('GanaGato', 'gana-gato');
      this.copyAlias('GanaGato', 'ganagato');
      this.copyAlias('MiProgol', 'prono-sports');
      this.copyAlias('MiProgol', 'pronosports');
      this.copyAlias('ProgolMediaSemana', 'progolmedia');
      this.copyAlias('ProgolMediaSemana', 'progol-media-semana');
      this.copyAlias('ProgolMediaSemana', 'progol ms');
    } catch (error) {
      console.warn('GameLogoService: no se pudieron cargar logos de electrónicos', error);
    }
  }

  async ensureLoaded(): Promise<void> {
    await this.ready;
  }

  getLogo(key: string, fallback: string): string {
    if (!key) return fallback;
    const product = this.resolveProductKey(key) || key;
    return this.byKey.get(normalizeLogoKey(product)) || this.byKey.get(normalizeLogoKey(key)) || fallback;
  }

  async getLogoAsync(key: string, fallback: string): Promise<string> {
    await this.ready;
    return this.getLogo(key, fallback);
  }

  /** Apply remote logos from gamesHome onto a hardcoded list by product/name. */
  async applyToList<
    T extends {
      name?: string;
      type?: string;
      dynamicLogoUrl?: string;
      logo?: string;
      path?: string[];
      product?: string;
    }
  >(items: T[]): Promise<T[]> {
    await this.ready;
    for (const item of items) {
      if (item.type === 'loteria' || item.type === 'tradicional') continue;

      const product = item.product || this.resolveProductFromItem(item);
      const url = product ? this.byKey.get(normalizeLogoKey(product)) : undefined;

      if (url) {
        item.dynamicLogoUrl = url;
      } else if (isRemoteLogoUrl(item.logo)) {
        item.dynamicLogoUrl = item.logo;
      }
    }
    return items;
  }

  getGames(): GameHome[] {
    return this.games;
  }

  /**
   * Melate → Melate (Revancha/Revanchita)
   * Melate Retro / path .../retro → MelateRetro
   */
  private resolveProductFromItem(item: { name?: string; logo?: string; path?: string[] }): string | undefined {
    if (item.path?.includes('retro')) return 'MelateRetro';
    if (item.path?.includes('media-semana')) return 'ProgolMediaSemana';

    const fromName = item.name ? this.resolveProductKey(item.name) : undefined;
    if (fromName) return fromName;

    const fromLogo = item.logo ? this.resolveProductKey(item.logo) : undefined;
    if (fromLogo) return fromLogo;

    if (item.path?.length) {
      const last = [...item.path].reverse().find((p) => p && p !== '/' && p !== 'game');
      if (last) return this.resolveProductKey(last);
    }

    return undefined;
  }

  private resolveProductKey(value: string): string | undefined {
    const normalized = value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
    const compact = normalizeLogoKey(value);

    return (
      UI_TO_PRODUCT[normalized] || UI_TO_PRODUCT[compact] || UI_TO_PRODUCT[normalized.replace(/\s+/g, '-')] || undefined
    );
  }

  private applyProductAliases(product: string, url: string): void {
    if (product === 'Melate') {
      // Nunca asociar MelateRetro aquí
      this.setKey('melaterevancha', url);
      this.setKey('melate-revancha-revanchita', url);
    } else if (product === 'MelateRetro') {
      this.setKey('melate-retro', url);
      this.setKey('melateretro', url);
      this.setKey('Melate Retro', url);
      // Importante: NO escribir la clave "melate"
    }
  }

  private setKey(key: string | undefined | null, url: string): void {
    if (!key) return;
    this.byKey.set(normalizeLogoKey(key), url);
  }

  private setKeyIfEmpty(key: string | undefined | null, url: string): void {
    if (!key) return;
    const normalized = normalizeLogoKey(key);
    if (!this.byKey.has(normalized)) {
      this.byKey.set(normalized, url);
    }
  }

  private copyAlias(from: string, to: string): void {
    const url = this.byKey.get(normalizeLogoKey(from));
    if (url) this.byKey.set(normalizeLogoKey(to), url);
  }
}

export { resolveGameLogoSrc, isRemoteLogoUrl };
